import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../components/AuthContext';
import { API_ENDPOINTS } from '../config/apiConfig';
import { saveAILayouts } from './AIHandler'; // Import the utility function
import { db } from '../firebaseConfig'; // Add this import for Firestore

const EditLayoutSuggestions = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Get data from location state
    const { suggestions, tileAnalysis, sessionId } = location.state || {};
    
    // Initialize layouts state from suggestions
    const [layouts, setLayouts] = useState([]);
    const [initialData, setInitialData] = useState(null);
    
    useEffect(() => {
        // Parse suggestions if they're provided
        if (suggestions) {
            try {
                console.log("Processing suggestions data:", suggestions);
                
                // Handle different formats of suggestion data
                let layoutsData = [];
                
                // If suggestions is already an object with layout_suggestions array
                if (suggestions.layout_suggestions && Array.isArray(suggestions.layout_suggestions)) {
                    console.log("Found layout_suggestions array:", suggestions.layout_suggestions);
                    layoutsData = suggestions.layout_suggestions;
                }
                // If suggestions is a string that might contain JSON
                else if (typeof suggestions === 'string') {
                    try {
                        const parsedData = JSON.parse(suggestions);
                        console.log("Parsed string suggestions:", parsedData);
                        
                        if (parsedData.layout_suggestions && Array.isArray(parsedData.layout_suggestions)) {
                            layoutsData = parsedData.layout_suggestions;
                        }
                    } catch (parseError) {
                        console.error("Failed to parse suggestions string:", parseError);
                    }
                }
                // If suggestions is an array directly
                else if (Array.isArray(suggestions)) {
                    console.log("Suggestions is already an array:", suggestions);
                    layoutsData = suggestions;
                }
                
                console.log("Final layouts data:", layoutsData);
                
                // Set the layouts state if we have data
                if (layoutsData && layoutsData.length > 0) {
                    setLayouts(layoutsData);
                    setInitialData(layoutsData);
                } else {
                    console.error("No valid layout suggestions found in:", suggestions);
                    setError("No layout suggestions found. Please return to the quilt builder and try again.");
                }
            } catch (err) {
                console.error("Failed to process suggestions:", err);
                setError("Failed to load layout suggestions. Please return to the quilt builder and try again.");
            }
        } else {
            console.error("No suggestion data provided");
            setError("No suggestion data provided. Please return to the quilt builder and try again.");
        }
    }, [suggestions]);

    // Handle title change
    const handleTitleChange = (index, newTitle) => {
        const updatedLayouts = [...layouts];
        updatedLayouts[index] = {
            ...updatedLayouts[index],
            title: newTitle
        };
        setLayouts(updatedLayouts);
    };

    // Handle description change - FIX: use newDescription as the parameter name
    const handleDescriptionChange = (index, newDescription) => {
        const updatedLayouts = [...layouts];
        updatedLayouts[index] = {
            ...updatedLayouts[index],
            description: newDescription
        };
        setLayouts(updatedLayouts);
    };

    // Handle generation of layouts
    const handleGenerateLayouts = async () => {
        if (!sessionId) {
            setError("Session ID is missing. Please return to the quilt builder and try again.");
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Format layout suggestions in the format expected by the API
            const formattedLayouts = layouts.map(layout => ({
                title: layout.title || layout.layout_name, // Handle both title and layout_name properties
                description: layout.description
            }));

            console.log("Sending formatted layouts to API:", formattedLayouts);

            // Call the generate-layouts endpoint
            const response = await fetch(API_ENDPOINTS.GENERATE_LAYOUTS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    layout_suggestions: formattedLayouts
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error generating layouts: ${errorText}`);
            }

            // Parse the response data
            const responseData = await response.json();
            console.log("Raw API response:", responseData);
            
            // Extract the layouts data from the response, handling different response formats
            let layoutsData;
            
            // If the response is wrapped in a 'suggestions' field as a string
            if (responseData.suggestions && typeof responseData.suggestions === 'string') {
                try {
                    // Try to extract JSON from the string
                    const jsonMatch = responseData.suggestions.match(/```(?:json)?\s*([\s\S]*?)```|(\{[\s\S]*\})/);
                    if (jsonMatch) {
                        const jsonContent = (jsonMatch[1] || jsonMatch[2]).trim();
                        layoutsData = JSON.parse(jsonContent);
                    } else {
                        // If no code blocks found, try parsing the whole string
                        layoutsData = JSON.parse(responseData.suggestions);
                    }
                } catch (parseError) {
                    console.error("Failed to parse layouts from string:", parseError);
                    throw new Error("Failed to parse AI response");
                }
            } else {
                // If suggestions is already an object
                layoutsData = responseData;
            }
            
            console.log("Processed layouts data:", layoutsData);
            
            // Validate we have layouts array
            if (!layoutsData || !layoutsData.layouts || !Array.isArray(layoutsData.layouts)) {
                throw new Error("Invalid layout data format from API");
            }
            
            // Save the layouts to Firestore
            if (currentUser && currentUser.uid) {
                const saved = await saveAILayouts(db, currentUser.uid, layoutsData.layouts);
                if (!saved) {
                    console.warn("Failed to save layouts to Firestore, but continuing");
                }
            }
            
            console.log("Layouts generated successfully:", layoutsData.layouts);

            // Navigate back to QuiltBuilder with a flag to indicate layouts are ready
            navigate('/QuiltBuilder', { 
                state: { 
                    aiLayoutsGenerated: true,
                    sessionId: sessionId
                }
            });

        } catch (err) {
            console.error("Failed to generate layouts:", err);
            setError(`Failed to generate layouts: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Return to QuiltBuilder if no data is available
    useEffect(() => {
        if (!suggestions && !location.state) {
            navigate('/QuiltBuilder');
        }
    }, [suggestions, location.state, navigate]);

    if (error) {
        return (
            <div className="bg-[#F6E8E0] min-h-screen flex flex-col items-center py-8">
                <div className="w-full max-w-4xl bg-white p-6 rounded-lg shadow-lg">
                    <div className="text-red-600 mb-4">{error}</div>
                    <button 
                        onClick={() => navigate('/QuiltBuilder')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                        Return to Quilt Builder
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#F6E8E0] min-h-screen flex flex-col items-center py-8">
            <div className="w-full max-w-4xl bg-white p-6 rounded-lg shadow-lg">
                <h1 className="text-2xl font-semibold mb-6 text-center" style={{ fontFamily: "Playfair Display" }}>
                    Customize Your Layout Suggestions
                </h1>
                
                <p className="text-gray-600 mb-6 text-center" style={{ fontFamily: "Playfair Display" }}>
                    Nostai has analyzed your quilt and created these layout suggestions. Feel free to edit the titles and descriptions before generating the final layouts.
                </p>
                
                <div className="flex justify-end mb-6">
                    <button 
                        onClick={handleGenerateLayouts}
                        disabled={isLoading}
                        className={`px-6 py-2 ${isLoading ? 'bg-blue-400' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md flex items-center`}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                Generating...
                            </>
                        ) : (
                            'Generate Layouts'
                        )}
                    </button>
                </div>
                
                <div className="space-y-6">
                    {layouts.map((layout, index) => (
                        <div key={index} className="border border-gray-300 rounded-lg p-4 hover:border-blue-300 transition-colors">
                            <div className="mb-3 flex items-center">
                                <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3">
                                    {index + 1}
                                </div>
                                <div className="relative flex-1">
                                    <input
                                        value={layout.title || layout.layout_name || ''}
                                        onChange={(e) => handleTitleChange(index, e.target.value)}
                                        className="w-full text-lg font-semibold border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                                        style={{ fontFamily: "Playfair Display" }}
                                        placeholder="Layout title"
                                    />
                                    <FontAwesomeIcon icon={faPencilAlt} className="text-gray-400 absolute right-2 top-3" />
                                </div>
                            </div>
                            
                            <div className="relative">
                                <textarea
                                    value={layout.description || ''}
                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                    className="w-full min-h-[80px] border rounded-md p-3 focus:border-blue-500 focus:outline-none"
                                    style={{ fontFamily: "Playfair Display" }}
                                    placeholder="Layout description"
                                />
                                <FontAwesomeIcon icon={faPencilAlt} className="text-gray-400 absolute right-3 top-3" />
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="mt-8 text-center">
                    <button 
                        onClick={handleGenerateLayouts}
                        disabled={isLoading}
                        className={`px-8 py-3 ${isLoading ? 'bg-blue-400' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-lg flex items-center justify-center mx-auto`}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                Generating Your Layouts...
                            </>
                        ) : (
                            'Generate Layouts'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditLayoutSuggestions;
